import { z } from 'zod'

const optionalNumber = z
  .union([z.number(), z.string()])
  .transform(v => (typeof v === 'string' ? Number(v) : v))
  .optional()

/**
 * Wire schemas for Vision's Pulse-facing surface.
 *
 * Kept isomorphic with the Java records in
 * `vision/src/main/java/in/boldfit/vision/adapter/in/http/`:
 *   - `OrdersQueryController` (list + detail)
 *   - `OrderAcknowledgementController` (ACK)
 *   - `OrderAuditController` (audit trail)
 *
 * Vision's `OrderStatus` enum lives at
 * `vision/src/main/java/in/boldfit/vision/domain/OrderStatus.java`. Pulse list
 * `status=` is the operator pipeline (received / partial-full ack / partial-full
 * fulfill / cancelled / closed). `ACKNOWLEDGED` is a legacy alias for fully acked.
 */

export const OrderStatusSchema = z.enum([
  'RECEIVED',
  'VALIDATION_FAILED',
  'READY_TO_ACKNOWLEDGE',
  'ACKNOWLEDGING',
  'ACKNOWLEDGED',
  'PARTIALLY_ACKED',
  'FULLY_ACKED',
  'PARTIALLY_CANCELLED',
  'PARTIALLY_FULFILLED',
  'FULFILLED',
  'CANCELLED',
  'CLOSED',
])
export type OrderStatus = z.infer<typeof OrderStatusSchema>

/**
 * Where a PO stands on Business Central Sales Orders, rolled up across its
 * warehouse waves — the `bcSalesOrder` filter on `GET /v1/orders`.
 *
 * A PO fans out to N waves and each wave gets its own Sales Order, so this is
 * deliberately not a boolean. The two values that carry the most weight:
 *
 *   - `partial` — some waves reached BC and some provably did not. Nothing else
 *     on the queue says so, and it is the state an operator most needs to find.
 *   - `unknown` — Vision could not read BC state for at least one wave. NOT the
 *     same as `none`: telling someone to create a Sales Order that may already
 *     exist is how a duplicate lands in BC. Vision keeps them apart, and so
 *     does the label.
 *
 * `no-waves` is separate from `none` because a PO that has not been sent to a
 * warehouse has no Vinculum SO for a Sales Order to exist against — there is
 * nothing to do in BC yet, so it does not belong in the actionable bucket.
 *
 * Posted invoice / IRN / e-way bill will be their own filter keys reusing these
 * same five values; that data is BC-side only today, so nothing here yet.
 */
export const BcSalesOrderFilterSchema = z.enum(['all', 'partial', 'none', 'unknown', 'no-waves'])
export type BcSalesOrderFilter = z.infer<typeof BcSalesOrderFilterSchema>

/** Menu order and copy. Each label states the rule it applies, not a mood. */
export const BC_SALES_ORDER_FILTERS = [
  { value: 'all', label: 'All SOs created' },
  { value: 'partial', label: 'Some SOs created' },
  { value: 'none', label: 'No SOs created' },
  { value: 'unknown', label: 'BC state not known' },
  { value: 'no-waves', label: 'Not sent to warehouse' },
] as const satisfies readonly { value: BcSalesOrderFilter; label: string }[]

export const BC_SALES_ORDER_FILTER_LABEL: Record<BcSalesOrderFilter, string> = Object.fromEntries(
  BC_SALES_ORDER_FILTERS.map(entry => [entry.value, entry.label]),
) as Record<BcSalesOrderFilter, string>

/** Operator pipeline shown in the Status filter. Legacy ACKNOWLEDGING stays parseable. */
export const FILTER_ORDER_STATUSES = [
  'RECEIVED',
  'READY_TO_ACKNOWLEDGE',
  'VALIDATION_FAILED',
  'PARTIALLY_ACKED',
  'FULLY_ACKED',
  'PARTIALLY_CANCELLED',
  'PARTIALLY_FULFILLED',
  'FULFILLED',
  'CANCELLED',
  'CLOSED',
] as const satisfies readonly OrderStatus[]

export const MoneySchema = z
  .object({
    amount: z
      .union([z.number(), z.string()])
      .transform(v => (typeof v === 'string' ? Number(v) : v)),
    currency: z.string(),
  })
  .nullable()
  .optional()
export type Money = z.infer<typeof MoneySchema>

export const OrderItemSchema = z.object({
  id: z.string().uuid().optional(),
  itemSequenceNumber: z.number().int(),
  channelSku: z.string(),
  // Amazon/Vision EAN when the line payload carries it. Optional so older
  // Vision builds still parse; Pulse falls back to catalog mappedSku.
  ean: z.string().nullable().optional(),
  orderedQuantity: z
    .union([z.number(), z.string()])
    .transform(v => (typeof v === 'string' ? Number(v) : v)),
  unitOfMeasure: z.string().nullable().optional(),
  netAmount: MoneySchema,
  imageUrl: z.string().nullable().optional(),
  displayName: z.string().nullable().optional(),
  unitSize: z.number().int().nullable().optional(),
  caseSize: z.number().int().nullable().optional(),
  ackCode: z.string().nullable().optional(),
  ackQuantity: z
    .union([z.number(), z.string()])
    .transform(v => (typeof v === 'string' ? Number(v) : v))
    .nullable()
    .optional(),
  ackReason: z.string().nullable().optional(),
  remainingToWarehouse: z
    .union([z.number(), z.string()])
    .transform(v => (typeof v === 'string' ? Number(v) : v))
    .nullable()
    .optional(),
  fulfilledQuantity: z
    .union([z.number(), z.string()])
    .transform(v => (typeof v === 'string' ? Number(v) : v))
    .nullable()
    .optional(),
  // Marketplace-owned withdrawal (for example Amazon's cancelledQuantity).
  // This is deliberately separate from an operator cancellation in Pulse.
  cancelledQuantity: z
    .union([z.number(), z.string()])
    .transform(v => (typeof v === 'string' ? Number(v) : v))
    .nullable()
    .optional(),
  manualCancelledQuantity: z
    .union([z.number(), z.string()])
    .transform(v => (typeof v === 'string' ? Number(v) : v))
    .nullable()
    .optional(),
  // Server-authoritative quantity still safe to cancel. Do not derive this
  // from ordered minus fulfilled: releases and channel cancellation also cap it.
  cancellableQuantity: z
    .union([z.number(), z.string()])
    .transform(v => (typeof v === 'string' ? Number(v) : v))
    .nullable()
    .optional(),
  lineStatus: OrderStatusSchema.nullable().optional(),
})
export type OrderItem = z.infer<typeof OrderItemSchema>

export const OrderWarehouseWaveSchema = z.object({
  seq: z.number().int(),
  // Vision `{PO}-{seq}` — Pulse primary SO. Older Vision builds omit the key.
  soNumber: z.string().nullable().optional(),
  // Assign-once Heimdall cutover owner. Only B2B_SHIPPED_V2 retires the
  // legacy Create/Add fallback; absent/null/LEGACY_V1 preserve it.
  automationOwner: z.string().nullable().optional(),
  // VineRetail eretail outputKey (`M77xx`, later `VKA0`) — copy + shell link only.
  vinculumSo: z.string().nullable().optional(),
  vinculumStatus: z.string().nullable().optional(),
  // PO line seqs on this wave (`fulfillment_line.item_sequence`). Older Vision
  // builds omit the key — Pulse then shows the wave on every sent line.
  lineItemSequenceNumbers: z.array(z.number().int()).optional(),
  // Business Central Sales Order state for this wave's Vinculum SO, from Heimdall
  // via Vision. `.nullable()` matters as much as `.optional()`: Heimdall emits
  // explicit `null`s rather than dropping keys, and `.optional()` alone rejects them.
  //
  // BC Page 42 `SalesOrder.No`. Only ever set alongside `bcStatus: 'CREATED'`.
  bcSalesOrderNo: z.string().nullable().optional(),
  // CREATED | PENDING | FAILED | NOT_ATTEMPTED. Left open as a string rather than
  // an enum so a state Heimdall adds later parses instead of failing the whole PO.
  //
  // NOT_ATTEMPTED and null are DIFFERENT facts and must render differently.
  // NOT_ATTEMPTED is Heimdall saying no real create has run for this SO — a
  // dry-run preview writes no row, so previewing leaves this state untouched.
  // Null/absent means Vision has no answer at all: the enrichment flag is off,
  // Heimdall did not respond, or it does not know this SO. "Not created yet" is
  // actionable; "we couldn't ask" is not.
  bcStatus: z.string().nullable().optional(),
  // Why the last real create attempt failed. Only ever set alongside
  // `bcStatus: 'FAILED'`. Free text from Heimdall — operator-facing, not a code.
  bcError: z.string().nullable().optional(),
  // PRESENT | ABSENT | UNKNOWN — whether Business Central holds lines for this
  // wave's Sales Order. Heimdall records the fact on `bc_sales_orders`; nothing
  // here derives it.
  //
  // The panel used to work this out from `vinculumStatus` (no lines unless
  // PACKED), which was true until Heimdall began capturing pack quantities at
  // `Pick complete` too. It then told operators that live, fully-lined Sales
  // Orders were waiting on packing. Read the fact, do not re-derive it.
  //
  // ABSENT and UNKNOWN are DIFFERENT facts, the same way NOT_ATTEMPTED and a
  // null `bcStatus` are. ABSENT is a Sales Order we know has no lines and is
  // worth a retry. UNKNOWN is every `bc_sales_orders` row written before
  // Heimdall's V55 — the count lives only in BC and cannot be backfilled, so it
  // is a permanent answer for historical POs — plus any wave Vision could not
  // enrich. Rendering UNKNOWN as "no lines" invites a retry that does nothing.
  bcLineState: z.string().nullable().optional(),
  // Lines on the Sales Order. Only ever set alongside `bcLineState: 'PRESENT'`.
  bcLineCount: z.number().int().nullable().optional(),
  // When the line-add step last settled. Only ever set alongside `PRESENT`.
  bcLinesAddedAt: z.string().nullable().optional(),
})
export type OrderWarehouseWave = z.infer<typeof OrderWarehouseWaveSchema>

export const OrderSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string(),
  channel: z.string(),
  channelLogoUrl: z.string().nullable().optional(),
  externalOrderNumber: z.string(),
  vendor: z.string().nullable().optional(),
  vendorName: z.string().nullable().optional(),
  // Amazon FC, or Flipkart CSV To OU ID (original ship-to). Flipkart preferred ignores this.
  shipToLocationCode: z.string().nullable().optional(),
  // Flipkart VSID (From OU ID), or Myntra Vendor GSTIN. Optional; Amazon omits.
  shippedFrom: z.string().nullable().optional(),
  // Vinculum locCodes preferred for this PO's ship-to / shipped-from.
  // From ship_to_hub / ship_from_hub — not inventory ATP.
  preferredHubs: z.array(z.string()).optional(),
  orderedAt: z.string().nullable().optional(),
  // Amazon delivery-window end — PO auto-cancel deadline. Optional so older
  // Vision builds still parse; pulse-v2 renders `—` when absent.
  cancelBy: z.string().nullable().optional(),
  createdAt: z.string(),
  status: OrderStatusSchema,
  items: z.array(OrderItemSchema),
  // OMS Send-to-warehouse waves. soNumber is `{PO}-{seq}`; vinculumSo is M77xx.
  waves: z.array(OrderWarehouseWaveSchema).optional(),
  // PO-level fulfilment totals from Vision GET /v1/orders. Money is paise
  // (INR × 100). Optional so older Vision builds still parse; Pulse falls
  // back to summing `items`.
  //
  // `sentToWarehouse*` and `remaining*` are release-accounting quantities.
  // `shipped*` is a legacy alias for sent-to-warehouse on this endpoint and is
  // intentionally not consumed: authoritative B2B shipped quantities live
  // only on `b2bShippedDocuments[].lines`.
  orderedQty: optionalNumber,
  orderedValuePaise: optionalNumber,
  sentToWarehouseQty: optionalNumber,
  sentToWarehouseValuePaise: optionalNumber,
  remainingQty: optionalNumber,
  remainingValuePaise: optionalNumber,
  shippedQty: optionalNumber,
  shippedValuePaise: optionalNumber,
  pendingQty: optionalNumber,
  pendingValuePaise: optionalNumber,
})
export type Order = z.infer<typeof OrderSchema>

export const OrderPageSchema = z.object({
  items: z.array(OrderSchema),
  page: z.number().int(),
  size: z.number().int(),
  total: z.number().int(),
})
export type OrderPage = z.infer<typeof OrderPageSchema>

const qtyOrValue = z
  .union([z.number(), z.string()])
  .transform(v => (typeof v === 'string' ? Number(v) : v))

export const FulfillableOrderSchema = z.object({
  order: OrderSchema,
  stockScope: z.enum(['PREFERRED', 'OTHER']),
  serviceableLineCount: z.number().int().nonnegative(),
  remainingQuantity: qtyOrValue,
  serviceableQuantity: qtyOrValue,
  preferredServiceableQuantity: qtyOrValue,
})
export type FulfillableOrder = z.infer<typeof FulfillableOrderSchema>

export const FulfillableOrdersResponseSchema = z.object({
  items: z.array(FulfillableOrderSchema),
  size: z.number().int().positive(),
  hasMore: z.boolean(),
  nextCursor: z.string().nullable().optional(),
})
export type FulfillableOrdersResponse = z.infer<typeof FulfillableOrdersResponseSchema>

/**
 * `GET /v1/orders/summary` — filtered-universe totals for the PO-home strip.
 * The UI reads only canonical `sentToWarehouse*` / `remaining*` release
 * totals. Ambiguous legacy `shipped*` aliases remain parseable but are never
 * used as B2B actual-shipped values.
 */
export const OrderSummarySchema = z.object({
  poCount: z.number(),
  orderedQty: qtyOrValue,
  orderedValue: qtyOrValue,
  sentToWarehouseQty: qtyOrValue,
  sentToWarehouseValue: qtyOrValue,
  remainingQty: qtyOrValue,
  remainingValue: qtyOrValue,
  shippedQty: qtyOrValue.optional(),
  shippedValue: qtyOrValue.optional(),
  pendingQty: qtyOrValue.optional(),
  pendingValue: qtyOrValue.optional(),
})
export type OrderSummary = z.infer<typeof OrderSummarySchema>

export const AcknowledgementResponseSchema = z.object({
  tenantId: z.string(),
  externalOrderNumber: z.string(),
  status: z.string(),
  amazonTransactionId: z.string().nullable().optional(),
  lines: z
    .array(
      z.object({
        itemSequenceNumber: z.number().int(),
        ackCode: z.string().nullable().optional(),
        ackQuantity: z.union([z.number(), z.string()]).nullable().optional(),
        ackReason: z.string().nullable().optional(),
      }),
    )
    .optional(),
})
export type AcknowledgementResponse = z.infer<typeof AcknowledgementResponseSchema>

export const AuditEventSchema = z.object({
  id: z.string().uuid(),
  ts: z.string(),
  eventType: z.string(),
  actor: z.string().nullable().optional(),
  correlationId: z.string().nullable().optional(),
  details: z.record(z.unknown()).nullable().optional(),
})
export type AuditEvent = z.infer<typeof AuditEventSchema>

export const AuditTrailSchema = z.object({
  externalOrderNumber: z.string(),
  tenantId: z.string(),
  events: z.array(AuditEventSchema),
})
export type AuditTrail = z.infer<typeof AuditTrailSchema>

export const ValidationSeveritySchema = z.enum(['FAIL', 'HOLD', 'WARN', 'INFO'])
export type ValidationSeverity = z.infer<typeof ValidationSeveritySchema>

export const ValidationResultSchema = z.enum(['PASSED', 'FAILED', 'HELD', 'WARNED'])
export type ValidationResult = z.infer<typeof ValidationResultSchema>

export const ValidationIssueSchema = z.object({
  code: z.string(),
  orderItemId: z.string().nullable().optional(),
  severity: ValidationSeveritySchema,
  blocking: z.boolean().optional(),
  message: z.string().nullable().optional(),
})
export type ValidationIssue = z.infer<typeof ValidationIssueSchema>

export const ValidationRunSchema = z.object({
  id: z.string(),
  orderId: z.string(),
  result: ValidationResultSchema,
  issues: z.array(ValidationIssueSchema),
  ranAt: z.string(),
  // Set by Vision when the GET auto-reruns a stale run in-line (e.g.
  // `LAST_RUN_STALE`, `MASTER_DATA_CHANGED`). Absent when the run wasn't
  // refreshed by the read — the UI keys the "Refreshed from Vision at …"
  // info banner off the presence of this field. Additive on both sides:
  // pulse-v2 tolerates it not being emitted, Vision tolerates old
  // clients that ignore it.
  staleReason: z.string().nullable().optional(),
})
export type ValidationRun = z.infer<typeof ValidationRunSchema>

export const ItemSchema = z.object({
  channel: z.string(),
  channelItemCode: z.string(),
  // Amazon CDN URLs are not always WHATWG-valid; don't use z.string().url().
  imageUrl: z.string().nullable().optional(),
  mappedSku: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  brand: z.string().nullable().optional(),
  category: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
})
export type Item = z.infer<typeof ItemSchema>

export const ItemsResponseSchema = z.object({
  items: z.array(ItemSchema),
})
export type ItemsResponse = z.infer<typeof ItemsResponseSchema>

export const ProblemSchema = z.object({
  type: z.string().url().optional(),
  title: z.string(),
  status: z.number().int(),
  detail: z.string().optional(),
  instance: z.string().optional(),
  correlationId: z.string().optional(),
  code: z.string().optional(),
  currentVersion: z.number().int().positive().optional(),
})
export type Problem = z.infer<typeof ProblemSchema>

/**
 * Directory schemas — mirror the Java records in
 * `vision/src/main/java/in/boldfit/vision/adapter/in/http/`:
 *   - `TenantsController` (GET /v1/tenants)
 *   - `ChannelsController` (GET /v1/channels?tenantCode=…)
 *   - `VendorsController`  (GET /v1/vendors?tenantCode=…)
 *
 * Every response wraps rows in `{ items: […] }` — matches the existing
 * items / orders surface so pulse-v2's query hooks all extract `.items`.
 */

export const TenantSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  displayName: z.string(),
  isActive: z.boolean(),
  createdAt: z.string(),
})
export type Tenant = z.infer<typeof TenantSchema>

export const TenantsResponseSchema = z.object({
  items: z.array(TenantSchema),
})
export type TenantsResponse = z.infer<typeof TenantsResponseSchema>

export const ChannelSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  code: z.string(),
  displayName: z.string(),
  channel: z.string(),
  marketplace: z.string().nullable().optional(),
  merchantId: z.string().nullable().optional(),
  logoUrl: z.string().nullable().optional(),
  isActive: z.boolean(),
  createdAt: z.string(),
})
export type Channel = z.infer<typeof ChannelSchema>

export const ChannelsResponseSchema = z.object({
  items: z.array(ChannelSchema),
})
export type ChannelsResponse = z.infer<typeof ChannelsResponseSchema>

export const VendorSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  code: z.string(),
  displayName: z.string(),
  legalName: z.string().nullable().optional(),
  gstin: z.string().nullable().optional(),
  isActive: z.boolean(),
  createdAt: z.string(),
})
export type Vendor = z.infer<typeof VendorSchema>

export const VendorsResponseSchema = z.object({
  items: z.array(VendorSchema),
})
export type VendorsResponse = z.infer<typeof VendorsResponseSchema>

export const WarehouseReleaseResponseSchema = z.object({
  externalOrderNumber: z.string(),
  waves: z.array(
    z.object({
      id: z.string().uuid().optional(),
      seq: z.number().int(),
      soNumber: z.string(),
      pickupLocation: z.string().nullable().optional(),
      vinculumSo: z.string().nullable().optional(),
    }),
  ),
})
export type WarehouseReleaseResponse = z.infer<typeof WarehouseReleaseResponseSchema>

export const WarehouseReleaseLineSchema = z.object({
  itemSequenceNumber: z.number().int(),
  quantity: z.number(),
  fulfillFrom: z.string().min(1),
})
export type WarehouseReleaseLine = z.infer<typeof WarehouseReleaseLineSchema>

export const BulkWarehouseReleaseLineSchema = z.object({
  itemSequenceNumber: z.number().int().positive(),
  originalQuantity: z.number().nullable(),
  quantity: z.number().int().positive(),
  fulfillFrom: z.string().min(1),
})
export type BulkWarehouseReleaseLine = z.infer<typeof BulkWarehouseReleaseLineSchema>

export const BulkWarehouseReleaseOrderSchema = z.object({
  externalPoNumber: z.string().min(1),
  priority: z.number().int().nonnegative(),
  lines: z.array(BulkWarehouseReleaseLineSchema).min(1),
})
export type BulkWarehouseReleaseOrder = z.infer<typeof BulkWarehouseReleaseOrderSchema>

export const BulkWarehouseReleaseRequestSchema = z.object({
  orders: z.array(BulkWarehouseReleaseOrderSchema).min(1),
})
export type BulkWarehouseReleaseRequest = z.infer<typeof BulkWarehouseReleaseRequestSchema>

export const BulkWarehouseReleaseStatusSchema = z.enum([
  'QUEUED',
  'PROCESSING',
  'COMPLETED',
  'PARTIAL',
  'FAILED',
])
export type BulkWarehouseReleaseStatus = z.infer<typeof BulkWarehouseReleaseStatusSchema>

export const BulkWarehouseReleaseItemStatusSchema = z.enum([
  'QUEUED',
  'PROCESSING',
  'CREATED',
  'FAILED',
])
export type BulkWarehouseReleaseItemStatus = z.infer<typeof BulkWarehouseReleaseItemStatusSchema>

export const BulkWarehouseReleaseLineStatusSchema = z.object({
  itemSequenceNumber: z.number().int().positive(),
  externalId: z.string().nullable().optional(),
  ean: z.string().nullable().optional(),
  displayName: z.string().nullable().optional(),
  originalQuantity: z.number().nullable(),
  quantity: z.number(),
  fulfillFrom: z.string(),
  unitPrice: optionalNumber.nullable(),
  totalPrice: optionalNumber.nullable(),
  currency: z.string().nullable().optional(),
})
export type BulkWarehouseReleaseLineStatus = z.infer<typeof BulkWarehouseReleaseLineStatusSchema>

export const BulkWarehouseReleaseItemSchema = z.object({
  id: z.string(),
  externalPoNumber: z.string(),
  priority: z.number().int(),
  fulfillFrom: z.string(),
  status: BulkWarehouseReleaseItemStatusSchema,
  soNumber: z.string().nullable().optional(),
  vinculumSo: z.string().nullable().optional(),
  errorCode: z.string().nullable().optional(),
  errorDetail: z.string().nullable().optional(),
  lines: z.array(BulkWarehouseReleaseLineStatusSchema).optional(),
})
export type BulkWarehouseReleaseItem = z.infer<typeof BulkWarehouseReleaseItemSchema>

export const BulkWarehouseReleaseResponseSchema = z.object({
  batchId: z.string(),
  status: BulkWarehouseReleaseStatusSchema,
  sourceChannel: z.string(),
  submittedBy: z.string(),
  createdAt: z.string(),
  completedAt: z.string().nullable().optional(),
  items: z.array(BulkWarehouseReleaseItemSchema),
})
export type BulkWarehouseReleaseResponse = z.infer<typeof BulkWarehouseReleaseResponseSchema>

export const BulkWarehouseReleaseBatchSummarySchema = z.object({
  batchId: z.string(),
  status: BulkWarehouseReleaseStatusSchema,
  sourceChannel: z.string(),
  submittedBy: z.string(),
  createdAt: z.string(),
  completedAt: z.string().nullable().optional(),
  poCount: z.number().int().nonnegative(),
  itemCount: z.number().int().nonnegative(),
  createdCount: z.number().int().nonnegative(),
  failedCount: z.number().int().nonnegative(),
})
export type BulkWarehouseReleaseBatchSummary = z.infer<
  typeof BulkWarehouseReleaseBatchSummarySchema
>

export const BulkWarehouseReleaseHistorySchema = z.object({
  items: z.array(BulkWarehouseReleaseBatchSummarySchema),
  page: z.number().int().nonnegative(),
  size: z.number().int().positive(),
  total: z.number().int().nonnegative(),
})
export type BulkWarehouseReleaseHistory = z.infer<typeof BulkWarehouseReleaseHistorySchema>

/**
 * `GET /v1/warehouses` item — Vision `{ code, name?, regionCode?, preferred? }`.
 * `preferred` is the ship_to_hub / ship_from_hub flag for this PO when Vision
 * scopes the list; Pulse never waits on inventory ATP for the badge.
 *
 * `inferredClass` is deprecated and optional. Ecom vs qcom used to be inferred
 * from the warehouse; it is now a property of the order's channel, so the field
 * is meaningless per warehouse and Vision will stop sending it. Optional here so
 * Pulse keeps parsing across that deploy in either order.
 */
export const WarehouseSchema = z.object({
  code: z.string().min(1),
  name: z.string().nullable().optional(),
  /** @deprecated ecom/qcom is a channel property now — read `channelClass` off the order. */
  inferredClass: z.string().nullable().optional(),
  /** Region whose MSL applies. Null when unmapped, which means no cap. */
  regionCode: z.string().nullable().optional(),
  preferred: z.boolean().optional(),
  shipTos: z.array(z.string()).optional(),
  shipFroms: z.array(z.string()).optional(),
})
export type Warehouse = z.infer<typeof WarehouseSchema>

/** Vision OpenAPI: GET /v1/warehouses returns `{ items }`, never a bare array. */
export const WarehousesResponseSchema = z.object({
  items: z.array(WarehouseSchema),
})
export type WarehousesResponse = z.infer<typeof WarehousesResponseSchema>

const numeric = z
  .union([z.number(), z.string()])
  .transform(v => (typeof v === 'string' ? Number(v) : v))

/**
 * One hub's availability for a line.
 *
 * `atp` is what this order's channel may draw and is what the UI shows. The rest
 * explains it: `available` is the physical unallocated stock, `mslShare` is this
 * hub's slice of its region's quick-commerce reserve, and for an ecom order
 * `atp === max(0, available - mslShare)`. For a qcom order no reserve applies
 * and `atp === available`.
 *
 * `qty` and `committedQty` are Vinculum's raw pair, reported but never applied —
 * `available` is `max(0, qty)` and the commitment does not reduce it. They are here
 * because `atp` alone cannot say which kind of zero a zero is: an empty shelf and a
 * commitment recorded against nothing are the same number and different jobs.
 * `committedQty > qty` is the Vinculum defect, currently on 541 prod rows.
 *
 * Both are absent on a Vision older than the feature and on a never-polled hub, so
 * neither may be read as 0 — see `warehouseStock`, which returns null for absent.
 *
 * `observed` false means this SKU × hub has never been polled. That is NOT zero
 * stock, and the two must not render the same way.
 */
export const OrderInventoryByWarehouseSchema = z.object({
  code: z.string(),
  atp: numeric,
  preferred: z.boolean().optional().default(false),
  /** @deprecated warehouse-level class is gone; use the order's `channelClass`. */
  inferredClass: z.string().nullable().optional(),
  available: numeric.nullable().optional(),
  qty: numeric.nullable().optional(),
  committedQty: numeric.nullable().optional(),
  mslShare: numeric.nullable().optional(),
  regionCode: z.string().nullable().optional(),
  regionMsl: numeric.nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  observed: z.boolean().optional().default(true),
})
export type OrderInventoryByWarehouse = z.infer<typeof OrderInventoryByWarehouseSchema>

/**
 * `GET /v1/orders/{po}/inventory` — ATP per line across every hub.
 * The channel class is applied server-side; Pulse displays `atp`.
 * `totalAtp` null/omitted with no warehouse rows = feed pending → `—`.
 */
export const OrderInventoryLineSchema = z.object({
  itemSequenceNumber: z.number().int(),
  // Vision LineInventory.erpSku — BC item number after ErpSkuResolver.
  // Optional so a Vision older than this key still parses.
  erpSku: z.string().nullable().optional(),
  totalAtp: numeric.nullable().optional(),
  byWarehouse: z.array(OrderInventoryByWarehouseSchema).optional().default([]),
})
export type OrderInventoryLine = z.infer<typeof OrderInventoryLineSchema>

export const OrderInventorySchema = z.object({
  lines: z.array(OrderInventoryLineSchema),
  /** ECOM or QCOM, resolved from this order's channel. Drives which formula produced `atp`. */
  channelClass: z.string().nullable().optional(),
  /**
   * Oldest observation behind these numbers — nothing here is fresher than this.
   * Null when nothing has ever been observed.
   *
   * True but incomplete on its own: one hub frozen for days dates the whole grid
   * to days while every live hub is minutes old. Read it with `newestUpdatedAt`.
   */
  asOf: z.string().nullable().optional(),
  /** Newest observation behind these numbers. Absent on a Vision older than this feature. */
  newestUpdatedAt: z.string().nullable().optional(),
  /**
   * How many SKU × hub rows Heimdall counted older than `stalenessHorizon`, across
   * the whole snapshot rather than any one line.
   *
   * Absent is NOT zero. Vision omits the key entirely (`non_null` inclusion) when
   * Heimdall predates the feature, and `0` is a real measurement — "nothing is
   * stale". The UI must hide the detail on absent and stay quiet on zero.
   */
  staleRows: z.number().int().nullable().optional(),
  /** How many rows Heimdall has never polled at all. Absent on the same terms. */
  neverObservedRows: z.number().int().nullable().optional(),
  /** ISO-8601 duration behind `staleRows`, e.g. `PT24H`. Absent on the same terms. */
  stalenessHorizon: z.string().nullable().optional(),
  /**
   * True when Vision could not reach the inventory feed. Distinct from genuine
   * zero stock, which the UI previously could not tell apart.
   */
  inventoryUnavailable: z.boolean().optional().default(false),
})
export type OrderInventory = z.infer<typeof OrderInventorySchema>

/** One row of `POST /v1/admin/msl/uploads`, proxied verbatim from Heimdall. */
export const MslUploadOutcomeSchema = z.object({
  rowNumber: z.number().int(),
  sku: z.string().nullable().optional(),
  regionCode: z.string().nullable().optional(),
  msl: z.number().nullable().optional(),
  status: z.enum(['ACCEPTED', 'WARNED', 'REJECTED']),
  message: z.string().nullable().optional(),
})
export type MslUploadOutcome = z.infer<typeof MslUploadOutcomeSchema>

export const MslUploadResultSchema = z.object({
  dryRun: z.boolean(),
  totalRows: z.number().int(),
  accepted: z.number().int(),
  rejected: z.number().int(),
  applied: z.number().int(),
  coverageRows: z.number(),
  outcomes: z.array(MslUploadOutcomeSchema).optional().default([]),
  parseErrors: z.array(z.string()).optional().default([]),
})
export type MslUploadResult = z.infer<typeof MslUploadResultSchema>

/**
 * One row of `GET /v1/admin/msl`, proxied verbatim from Heimdall's
 * `SkuMslListResponse.Item`. `msl` is a whole number of units — the reserve
 * held back from e-commerce for this SKU in this region.
 */
export const MslRowSchema = z.object({
  skuCode: z.string(),
  regionCode: z.string(),
  msl: z.number(),
  updatedAt: z.string().nullable().optional(),
  updatedBy: z.string().nullable().optional(),
})
export type MslRow = z.infer<typeof MslRowSchema>

/**
 * `coverageRows` is the total across **all** regions, not the count matching a
 * `regionCode` filter — see Heimdall's `SkuMslController#list`, which sources it
 * from an unfiltered `count()`. Paging must therefore terminate on a short page
 * rather than on `coverageRows`.
 */
export const MslListResponseSchema = z.object({
  items: z.array(MslRowSchema),
  coverageRows: z.number(),
  page: z.number().int(),
  size: z.number().int(),
})
export type MslListResponse = z.infer<typeof MslListResponseSchema>

/**
 * `GET /v1/sales-orders` and `GET /v1/sales-orders/{soNumber}` — the SO-centric
 * queue, one row per Vinculum Sales Order rather than one per PO.
 *
 * Vision runs `default-property-inclusion: non_null`, so **an absent key is not
 * a value**. Every fact below is `.nullable().optional()` for that reason, and
 * the two spellings are deliberately kept apart rather than normalised here:
 *
 *   - explicit `null` — Vision looked and there is nothing. The stage has not
 *     happened.
 *   - absent — Vision has no answer. Heimdall was unreachable, or the field
 *     predates the build that reports it.
 *
 * Zod does not set an absent optional key on the parsed object, so
 * `value === undefined` survives the parse and `salesOrderObservation` in
 * `@/features/sales-orders/journey` reads the two apart. Nothing in this file
 * may add `.default()` to these fields — a default collapses the distinction
 * at the schema boundary, where no caller can recover it.
 *
 * This is the same discrimination `features/orders/bc-so-state.ts` enforces
 * between `NOT_ATTEMPTED` and a null `bcStatus`, for the same reason: an
 * operator told to create a Sales Order that may already exist will make a
 * duplicate in Business Central.
 */

/**
 * The Vinculum warehouse ladder, in contract order.
 *
 * `SHORT_CLOSED` and `CANCELLED` are terminal outcomes rather than rungs — the
 * rail stops at them instead of staying grey forever — and
 * `ALLOCATION_REJECTED` is off-ladder entirely: the SO exists and can still
 * allocate once stock lands. See `@/lib/vinculum-warehouse-label`, which owns
 * the labels, the tones, and those predicates.
 */
export const VINCULUM_LADDER_STATUSES = [
  'SO_CREATED',
  'ALLOCATION_REJECTED',
  'ALLOCATED',
  'PICKED',
  'PACKED',
  'INVOICED',
  'PARTIALLY_SHIPPED',
  'SHIPPED',
  'DELIVERED',
  'SHORT_CLOSED',
  'CANCELLED',
] as const
export type VinculumLadderStatus = (typeof VINCULUM_LADDER_STATUSES)[number]

/**
 * Vinculum holding stock against this SO that Heimdall cannot account for.
 *
 * Its own object rather than a `vinculumStatus` token because it is orthogonal
 * to the ladder — an SO can be commitment-unreconciled at any rung. It exists
 * at all because `FAILED` was overloaded to mean two opposite things and hid 33
 * orders holding 12,371 units of committed stock, so nothing here may render as
 * a flavour of failure.
 */
export const VinculumCommitmentSchema = z.object({
  /** `OPEN` while unreconciled. Bare string so a state Vision adds later parses. */
  state: z.string().nullable().optional(),
  /** What raised it, e.g. `ALLOCATE_AMBIGUOUS` — an allocate call whose outcome was never observable. */
  trigger: z.string().nullable().optional(),
  /** Whether Vinculum is known to be holding stock. Absent is not `false`. */
  knownCommitted: z.boolean().nullable().optional(),
  detectedAt: z.string().nullable().optional(),
})
export type VinculumCommitment = z.infer<typeof VinculumCommitmentSchema>

export const SalesOrderRowSchema = z.object({
  /** Vision `{PO}-{seq}`. The route key, and the only field that cannot be absent. */
  soNumber: z.string(),
  seq: z.number().int(),
  externalPoNumber: z.string(),
  channel: z.string().nullable().optional(),
  vendorCode: z.string().nullable().optional(),
  vendorName: z.string().nullable().optional(),
  buyerEntity: z.string().nullable().optional(),
  /** Vinculum hub the PO asked to ship from. May differ from the site that fulfilled it. */
  pickupLocation: z.string().nullable().optional(),
  heimdallCorrelationId: z.string().nullable().optional(),
  /** VineRetail eRetail outputKey (`M10130510`). Absent until Vinculum answers order/create. */
  vinculumSo: z.string().nullable().optional(),
  vinculumSourceOrderNo: z.string().nullable().optional(),
  /** One of {@link VINCULUM_LADDER_STATUSES}, left a bare string so a widened ladder parses. */
  vinculumStatus: z.string().nullable().optional(),
  /** When the SO entered its current status — the clock behind "in stage" and `stuckOverHours`. */
  vinculumStatusChangedAt: z.string().nullable().optional(),
  vinculumSyncedAt: z.string().nullable().optional(),
  vinculumCommitment: VinculumCommitmentSchema.nullable().optional(),
  bcSalesOrderNo: z.string().nullable().optional(),
  /** CREATED | PENDING | FAILED | NOT_ATTEMPTED. Read through `bcSoState`, never switched on here. */
  bcStatus: z.string().nullable().optional(),
  bcSyncedAt: z.string().nullable().optional(),
  /**
   * Heimdall → Vision posting + e-invoice summary (frozen Pulse contract).
   *
   *   postingState, postedInvoiceNo | postedInvoiceNos, postingSettledAt,
   *   eInvoiceOutcome (NOT_ISSUED|ISSUED|INCOMPLETE_RECEIPT|CANCELLED),
   *   irn, irnObservedAt
   *
   * Same absent-vs-null rule as `bcStatus`. Absent on today's queue → journey
   * omits invoice/IRN pips (detail still shows them as notVisible). Never
   * `.default()` — that would collapse the discrimination journey.ts needs.
   */
  postingState: z.string().nullable().optional(),
  postedInvoiceNo: z.string().nullable().optional(),
  postedInvoiceNos: z.array(z.string()).nullable().optional(),
  postingSettledAt: z.string().nullable().optional(),
  eInvoiceOutcome: z.string().nullable().optional(),
  irn: z.string().nullable().optional(),
  irnObservedAt: z.string().nullable().optional(),
  orderedQuantity: numeric.nullable().optional(),
  /** PO line seqs on this SO. */
  lineItemSequenceNumbers: z.array(z.number().int()).optional(),
  createdAt: z.string().nullable().optional(),
})
export type SalesOrderRow = z.infer<typeof SalesOrderRowSchema>

export const PagedSalesOrderResponseSchema = z.object({
  items: z.array(SalesOrderRowSchema),
  page: z.number().int(),
  size: z.number().int(),
  total: z.number().int(),
})
export type PagedSalesOrderResponse = z.infer<typeof PagedSalesOrderResponseSchema>

/**
 * One entry from Heimdall's append-only `order_events`, newest first.
 *
 * An entry where `from === to` is an **observation**, not a transition —
 * something Heimdall noticed without the SO moving — and the timeline renders
 * it differently. `from` absent means the same: nothing moved.
 */
export const SalesOrderEventSchema = z.object({
  at: z.string(),
  from: z.string().nullable().optional(),
  to: z.string(),
  reason: z.string().nullable().optional(),
})
export type SalesOrderEvent = z.infer<typeof SalesOrderEventSchema>

/**
 * One SKU on the Sales Order, with the three quantities kept as three separate
 * readings so a short pack is visible rather than inferred.
 *
 * `allocated` and `packed` carry the absent-vs-null distinction that the whole
 * screen turns on:
 *
 *   - `null` — that stage has not happened. Renders "not allocated".
 *   - absent — Heimdall was unreachable. Renders "not visible".
 *
 * Collapsing them is the defect this screen exists not to reintroduce, so
 * neither may gain a `.default()`.
 */
export const SalesOrderLineSchema = z.object({
  sku: z.string(),
  requested: numeric.nullable().optional(),
  allocated: numeric.nullable().optional(),
  packed: numeric.nullable().optional(),
})
export type SalesOrderLine = z.infer<typeof SalesOrderLineSchema>

export const B2bShippedLineSchema = z.object({
  sourceLineId: z.string(),
  sku: z.string(),
  /** Fixed-scale immutable quantity; kept as text so Pulse never rounds it. */
  shippedQty: z.string().regex(/^(?:0|[1-9]\d{0,33})\.\d{4}$/),
})
export type B2bShippedLine = z.infer<typeof B2bShippedLineSchema>

/**
 * One invoice/site shipment group attached to this fulfillment.
 *
 * Its stage/status are immutable attachment-time audit fields. Current
 * operator state comes from `b2bShippedAutomations`; these stay open strings
 * so a newer audit token cannot make the whole detail unparsable.
 */
export const B2bShippedDocumentSchema = z.object({
  shipmentGroupId: z.string().uuid(),
  projectionRevision: z.number().int().positive(),
  automationStage: z.string(),
  automationStatus: z.string(),
  automationDetail: z.string().nullable().optional(),
  lastConflictCode: z.string().nullable().optional(),
  invoiceNumber: z.string(),
  fulfillmentSite: z.string(),
  externalDocumentNo: z.string(),
  bcSalesOrderNo: z.string(),
  bcUrl: z.string(),
  snapshotHash: z.string(),
  operationId: z.string(),
  requestHash: z.string(),
  attachedAt: z.string(),
  lines: z.array(B2bShippedLineSchema),
})
export type B2bShippedDocument = z.infer<typeof B2bShippedDocumentSchema>

/**
 * Current CAS-backed Norn stage/status. Unlike a document attachment, this
 * exists before Business Central or the true-shipped projection is finalized.
 */
export const B2bShippedAutomationSchema = z.object({
  shipmentGroupId: z.string().uuid(),
  /**
   * LEGACY_V1 | B2B_SHIPPED_V2. Nullable/optional for rolling compatibility;
   * absence never authorizes retiring the legacy fallback.
   */
  automationOwner: z.string().nullable().optional(),
  /** LIVE | SHADOW. */
  mode: z.string(),
  /** WAITING_FOR_CARTONS | CREATING_BC | SYNCING_PULSE | COMPLETED. */
  stage: z.string(),
  /** RUNNING | WAITING | PARKED | FAILED | COMPLETED | SHADOW_NO_WRITE. */
  status: z.string(),
  stateRevision: z.number().int().positive(),
  stateSequence: z.number().int().positive(),
  stateHash: z.string(),
  workflowId: z.string(),
  runId: z.string().uuid(),
  snapshotRevision: z.number().int().positive(),
  snapshotHash: z.string(),
  invoiceNumber: z.string(),
  fulfillmentSite: z.string(),
  operationId: z.string(),
  requestHash: z.string().nullable().optional(),
  externalDocumentNo: z.string(),
  bcSalesOrderNo: z.string().nullable().optional(),
  detail: z.string().nullable().optional(),
  conflictCode: z.string().nullable().optional(),
  occurredAt: z.string(),
  updatedAt: z.string(),
})
export type B2bShippedAutomation = z.infer<typeof B2bShippedAutomationSchema>

/**
 * Known Heimdall e-invoice outcomes. Wire fields stay bare strings on the row
 * so unknown tokens still parse; `journey.ts` maps these four explicitly.
 */
export const EInvoiceOutcomeSchema = z.enum([
  'NOT_ISSUED',
  'ISSUED',
  'INCOMPLETE_RECEIPT',
  'CANCELLED',
])
export type EInvoiceOutcome = z.infer<typeof EInvoiceOutcomeSchema>

/** `GET /v1/sales-orders/{soNumber}` — every list fact plus the journey detail. */
export const SalesOrderDetailSchema = SalesOrderRowSchema.extend({
  /** Where Vinculum actually fulfilled from. Differs from `pickupLocation` more often than anyone expects. */
  vinculumFulfillmentSite: z.string().nullable().optional(),
  vinculumOrderPk: z.string().nullable().optional(),
  lastFailureReason: z.string().nullable().optional(),
  bcError: z.string().nullable().optional(),
  /** PRESENT | ABSENT | UNKNOWN — read through `bcLineState`, which keeps ABSENT and UNKNOWN apart. */
  bcLineState: z.string().nullable().optional(),
  bcLineCount: z.number().int().nullable().optional(),
  bcLinesAddedAt: z.string().nullable().optional(),
  /** Fulfillment columns. Omitted on a live wave; not carried on the queue row. */
  cancelledAt: z.string().nullable().optional(),
  cancelledBy: z.string().nullable().optional(),
  cancellationReason: z.string().nullable().optional(),
  cancellationRemarks: z.string().nullable().optional(),
  events: z.array(SalesOrderEventSchema).optional(),
  lines: z.array(SalesOrderLineSchema).optional(),
  /** One fulfillment can produce several immutable invoice-group BC documents. */
  b2bShippedDocuments: z.array(B2bShippedDocumentSchema).nullable().optional(),
  /** Current per-group state, available before a document exists. */
  b2bShippedAutomations: z.array(B2bShippedAutomationSchema).nullable().optional(),
})
export type SalesOrderDetail = z.infer<typeof SalesOrderDetailSchema>

/**
 * `POST /v1/sales-orders/{soNumber}/cancel` body. `reason` is mandatory; the
 * only Vinculum-attested token today is `Others`. Pulse ships a select.
 */
export const CancelSalesOrderReasonSchema = z.enum(['Others'])
export type CancelSalesOrderReason = z.infer<typeof CancelSalesOrderReasonSchema>

export const CancelSalesOrderRequestSchema = z.object({
  reason: CancelSalesOrderReasonSchema,
  remarks: z.string().optional(),
  acknowledgePicklist: z.boolean(),
})
export type CancelSalesOrderRequest = z.infer<typeof CancelSalesOrderRequestSchema>

export const CancelSalesOrderOutcomeSchema = z.enum([
  'CANCELLED',
  'ALREADY_CANCELLED',
  'SHADOWED',
  'REFUSED',
  'UNALLOCATED_CANCEL_FAILED',
])
export type CancelSalesOrderOutcome = z.infer<typeof CancelSalesOrderOutcomeSchema>

export const CancelSalesOrderModeSchema = z.enum(['off', 'shadow', 'live'])
export type CancelSalesOrderMode = z.infer<typeof CancelSalesOrderModeSchema>

export const CancelSalesOrderPreflightSchema = z.object({
  rawStatus: z.string().nullable().optional(),
  picklistGenerated: z.boolean(),
  pickDate: z.string().nullable().optional(),
  packDate: z.string().nullable().optional(),
  committedLineCount: z.number().int(),
  bcSalesOrderExists: z.boolean(),
})
export type CancelSalesOrderPreflight = z.infer<typeof CancelSalesOrderPreflightSchema>

export const CancelSalesOrderCallSchema = z.object({
  endpoint: z.string(),
  uniqueKey: z.string().nullable().optional(),
  dryRun: z.boolean(),
})
export type CancelSalesOrderCall = z.infer<typeof CancelSalesOrderCallSchema>

/**
 * Same envelope on 200, Heimdall 409, and Heimdall-shaped 502. `outcome` is
 * required so an RFC 7807 ProblemDetail cannot parse as a cancel result.
 */
export const CancelSalesOrderResponseSchema = z.object({
  outcome: CancelSalesOrderOutcomeSchema,
  mode: CancelSalesOrderModeSchema.nullable().optional(),
  refusal: z.string().nullable().optional(),
  preflight: CancelSalesOrderPreflightSchema.nullable().optional(),
  calls: z.array(CancelSalesOrderCallSchema).nullable().optional(),
  salesOrder: SalesOrderDetailSchema.nullable().optional(),
})
export type CancelSalesOrderResponse = z.infer<typeof CancelSalesOrderResponseSchema>

/**
 * `POST /v1/sales-orders/{soNumber}/warehouse-status:refresh` — Operator+ only.
 * Vision pulls Heimdall `/orders/{id}/refresh` then applies the wave.
 */
export const RefreshWarehouseStatusResponseSchema = z.object({
  soNumber: z.string(),
  vinculumSo: z.string().nullable().optional(),
  vinculumStatus: z.string().nullable().optional(),
  heimdallRefreshed: z.boolean(),
  linesAdjusted: z.number().int(),
  statusAdvanced: z.boolean(),
})
export type RefreshWarehouseStatusResponse = z.infer<typeof RefreshWarehouseStatusResponseSchema>

/** `warehouseStatus=` — the ladder plus the "Vision has no answer" bucket. */
export const SalesOrderWarehouseFilterSchema = z.enum([...VINCULUM_LADDER_STATUSES, 'UNKNOWN'])
export type SalesOrderWarehouseFilter = z.infer<typeof SalesOrderWarehouseFilterSchema>

/**
 * `bcStatus=` — Heimdall's four, with `UNKNOWN` as its own selectable bucket.
 *
 * `UNKNOWN` is a filter value rather than the absence of one because "show me
 * the SOs whose BC state nobody can see" is the query an operator most needs
 * and cannot otherwise express.
 */
export const SalesOrderBcFilterSchema = z.enum(['CREATED', 'PENDING', 'FAILED', 'UNKNOWN'])
export type SalesOrderBcFilter = z.infer<typeof SalesOrderBcFilterSchema>

/** `sort=` — `-timeInStage` first, because the queue's job is finding what stopped moving. */
export const SalesOrderSortSchema = z.enum([
  '-timeInStage',
  'timeInStage',
  '-createdAt',
  'createdAt',
  'soNumber',
])
export type SalesOrderSort = z.infer<typeof SalesOrderSortSchema>

/**
 * Pulse access roles — wire names match Vision / Entra app roles.
 * UI labels: Superadmin / Admin / Config Manager / Operator / Viewer.
 */
export const PulseRoleSchema = z.enum([
  'BoldfitSuperadmin',
  'BoldfitAdmin',
  'BoldfitConfigManager',
  'BoldfitManager',
  'BoldfitViewer',
])
export type PulseRole = z.infer<typeof PulseRoleSchema>

export const MeSchema = z.object({
  subject: z.string(),
  email: z.string().nullable().optional(),
  displayName: z.string().nullable().optional(),
  role: PulseRoleSchema,
})
export type Me = z.infer<typeof MeSchema>

export const AmazonPoStatusOutcomeSchema = z.enum([
  'CORRECTED',
  'WOULD_CORRECT',
  'REPUBLISHED',
  'WOULD_REPUBLISH',
  'UNCHANGED',
  'BLOCKED_INCREASE',
  'NOT_HELD_LOCALLY',
  'NOT_FOUND_AT_AMAZON',
  'NOT_OBSERVABLE',
  'ERRORED',
])

export const AmazonPoStatusResultSchema = z.object({
  poNumber: z.string(),
  outcome: AmazonPoStatusOutcomeSchema,
  lwaAccount: z.string().nullable().optional(),
  storedState: z.string().nullable().optional(),
  amazonState: z.string().nullable().optional(),
  acceptedUnitsBefore: z.number(),
  acceptedUnitsAfter: z.number(),
  cancelledUnitsBefore: z.number(),
  cancelledUnitsAfter: z.number(),
  linesChanged: z.number().int(),
  revision: z.number().int().nullable().optional(),
  detail: z.string().nullable().optional(),
})

export const AmazonPoStatusReadResponseSchema = z.object({
  tenantId: z.string(),
  dryRun: z.boolean(),
  allowIncreases: z.boolean(),
  forceRepublish: z.boolean(),
  requested: z.number().int(),
  summary: z.object({
    corrected: z.number().int(),
    republished: z.number().int(),
    unchanged: z.number().int(),
    blockedIncreases: z.number().int(),
    notHeldLocally: z.number().int(),
    notFoundAtAmazon: z.number().int(),
    notObservable: z.number().int(),
    errored: z.number().int(),
    unitsIncreased: z.number(),
    unitsDecreased: z.number(),
  }),
  results: z.array(AmazonPoStatusResultSchema),
})

export type AmazonPoStatusReadResponse = z.infer<typeof AmazonPoStatusReadResponseSchema>
export type AmazonPoStatusResult = z.infer<typeof AmazonPoStatusResultSchema>

export const InventoryReportSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  filename: z.string(),
})
export type InventoryReport = z.infer<typeof InventoryReportSchema>

export const InventoryReportCatalogSchema = z.object({
  reports: z.array(InventoryReportSchema),
})
export type InventoryReportCatalog = z.infer<typeof InventoryReportCatalogSchema>

export const AdminChannelSyncTypeSchema = z.enum(['MSD_PRICES', 'VINCULUM'])
export type AdminChannelSyncType = z.infer<typeof AdminChannelSyncTypeSchema>

export const AdminChannelSyncStartedSchema = z.object({
  runId: z.string().uuid(),
  type: AdminChannelSyncTypeSchema,
  channel: z.string(),
})
export type AdminChannelSyncStarted = z.infer<typeof AdminChannelSyncStartedSchema>

export const AdminChannelSyncRunSchema = z.object({
  runId: z.string().uuid(),
  type: AdminChannelSyncTypeSchema,
  channel: z.string(),
  outcome: z.enum(['RUNNING', 'OK', 'FAILED', 'SKIPPED', 'ABANDONED']),
  running: z.boolean(),
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
  examined: z.number().int().nullable(),
  updated: z.number().int().nullable(),
  closed: z.number().int().nullable(),
  unresolved: z.number().int().nullable(),
  skipped: z.number().int().nullable(),
  error: z.string().nullable(),
})
export type AdminChannelSyncRun = z.infer<typeof AdminChannelSyncRunSchema>
